import { ArrowRight, ClipboardPaste, FileWarning, Trash2, Upload } from 'lucide-react'
import { useState } from 'react'

import { Aviso, Boton } from '../../componentes/ui'
import { type DocumentoSinLeer, MINIMO_DE_TEXTO_PEGADO, nombreDelTextoPegado } from './estado'

const ACEPTADOS = '.pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown'

type Props = {
  documentos: DocumentoSinLeer[]
  /** Mientras se monta la web: nada se puede tocar. */
  continuando: boolean
  /** Sube el fichero en lugar del que no se leyó; `true` si esta vez se ha leído. */
  leer: (id: number, fichero: File) => Promise<boolean>
  quitar: (id: number) => void
  continuar: () => void
}

/**
 * Los documentos del brief que no se han podido leer al crear la web (Alta.tsx): el alta no
 * sigue hasta que el cliente decide qué hacer con cada uno —subir otro fichero, pegar su texto
 * o quitarlo—, o sigue sin ellos. Es lo que hace posible lo que le pide el mensaje del núcleo:
 * con el alta todavía en «materiales», el núcleo admite otro documento.
 */
export const DocumentosSinLeer = ({ documentos, continuando, leer, quitar, continuar }: Props) => (
  <section aria-labelledby="documentos-sin-leer" className="rounded-caja border border-linea bg-papel p-5 md:p-6">
    <h2 className="font-display text-base font-semibold" id="documentos-sin-leer">
      {documentos.length === 0 ? 'Ya tenemos todo lo que nos diste' : documentos.length === 1 ? 'Tu web ya está creada, pero un documento no se ha podido leer' : `Tu web ya está creada, pero ${documentos.length} documentos no se han podido leer`}
    </h2>
    <p className="mt-1 text-[13px] text-tenue">
      {documentos.length === 0
        ? 'Sigue cuando quieras: se montan las páginas, se redactan los textos y se construye la previa.'
        : 'Antes de montar la web, decide qué hacer con cada uno: sube otro fichero, pega su texto o quítalo. Con lo que no se lea no se redacta nada.'}
    </p>

    {documentos.length > 0 && (
      <ul className="mt-4 flex flex-col gap-3">
        {documentos.map((d) => (
          <Documento continuando={continuando} documento={d} key={d.id} leer={(f) => leer(d.id, f)} quitar={() => quitar(d.id)} />
        ))}
      </ul>
    )}

    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-linea pt-4">
      <p className="max-w-prose text-xs text-tenue">
        {documentos.length === 0
          ? 'Tus documentos ya están leídos.'
          : 'Si sigues sin él, la IA redacta con el resto de lo que nos has dado, y lo que no dé para escribir se queda por rellenar para que lo escribas tú.'}
      </p>
      <Boton cargando={continuando} icono={<ArrowRight />} onClick={continuar} tipo="primario">
        {documentos.length === 0 ? 'Continuar el alta' : documentos.length === 1 ? 'Seguir sin ese documento' : 'Seguir sin esos documentos'}
      </Boton>
    </div>
  </section>
)

const Documento = ({ documento, continuando, leer, quitar }: { continuando: boolean; documento: DocumentoSinLeer; leer: (fichero: File) => Promise<boolean>; quitar: () => void }) => {
  const [pegando, setPegando] = useState(false)
  const [texto, setTexto] = useState('')
  const [leyendo, setLeyendo] = useState<null | string>(null)
  const ocupado = continuando || leyendo !== null
  const caracteres = texto.trim().length

  const probar = async (fichero: File, que: string) => {
    setLeyendo(que)
    try {
      // Si se lee, el documento sale de la lista y este componente con él; si no, el mensaje nuevo sale arriba.
      await leer(fichero)
    } finally {
      setLeyendo(null)
    }
  }

  return (
    <li className="rounded-caja border border-linea bg-papel-2 p-4">
      <p className="flex items-center gap-2 text-sm font-medium">
        <FileWarning aria-hidden className="size-4 shrink-0 text-error" />
        <span className="truncate">{documento.nombre}</span>
      </p>
      <div className="mt-2">
        <Aviso tono="error">{documento.mensaje}</Aviso>
      </div>

      {pegando ? (
        <div className="mt-3">
          <label className="text-[13px] font-medium" htmlFor={`texto-de-${documento.id}`}>
            El texto de «{documento.nombre}»
          </label>
          <textarea
            className="campo mt-1 w-full rounded-campo border border-linea-fuerte bg-papel px-3 py-2 text-sm"
            disabled={ocupado}
            id={`texto-de-${documento.id}`}
            maxLength={60_000}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Abre el documento, selecciona todo su texto, cópialo y pégalo aquí."
            rows={8}
            value={texto}
          />
          <p className="mt-1 text-xs text-tenue">{caracteres < MINIMO_DE_TEXTO_PEGADO ? `Al menos ${MINIMO_DE_TEXTO_PEGADO} caracteres (llevas ${caracteres}).` : `${caracteres} caracteres. Se guarda como «${nombreDelTextoPegado(documento.nombre)}».`}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Boton
              cargando={leyendo === 'texto'}
              disabled={ocupado || caracteres < MINIMO_DE_TEXTO_PEGADO}
              onClick={() => void probar(new File([texto.trim()], nombreDelTextoPegado(documento.nombre), { type: 'text/plain' }), 'texto')}
              pequeno
              tipo="primario"
            >
              Guardar el texto
            </Boton>
            <Boton disabled={ocupado} onClick={() => setPegando(false)} pequeno tipo="discreto">
              Cancelar
            </Boton>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <label
            aria-disabled={ocupado}
            className={`inline-flex h-8 items-center gap-1.5 rounded-boton border border-linea bg-papel px-3 text-[13px] font-medium shadow-suave ${ocupado ? 'cursor-not-allowed text-tenue' : 'cursor-pointer hover:border-linea-fuerte hover:bg-papel-2'}`}
          >
            <Upload aria-hidden className="size-4" /> {leyendo === 'fichero' ? 'Leyendo…' : 'Subir otro fichero'}
            <input
              accept={ACEPTADOS}
              aria-label={`Otro fichero en lugar de «${documento.nombre}»`}
              className="sr-only"
              disabled={ocupado}
              onChange={(e) => {
                const fichero = e.target.files?.[0]
                e.target.value = ''
                if (fichero) void probar(fichero, 'fichero')
              }}
              type="file"
            />
          </label>
          <Boton disabled={ocupado} icono={<ClipboardPaste />} onClick={() => setPegando(true)} pequeno>
            Pegar su texto
          </Boton>
          <Boton aria-label={`Quitar «${documento.nombre}»`} disabled={ocupado} icono={<Trash2 />} onClick={quitar} pequeno tipo="peligro">
            Quitarlo
          </Boton>
        </div>
      )}
    </li>
  )
}
